import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { XCircle, CheckCircle2, FolderPlus, Loader2 } from 'lucide-react';
import { formatCurrency } from '../crmUtils';
import { CurrencyInput } from '../../ui/currency-input';

export const DescarteDialog = ({ open, onOpenChange, descarteData, setDescarteData, motivosDescarte, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <XCircle className="h-5 w-5" />
          Descartar Oportunidade
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Motivo do Descarte *</Label>
          <Select value={descarteData.motivo} onValueChange={v => setDescarteData(p => ({ ...p, motivo: v }))}>
            <SelectTrigger><SelectValue placeholder="Selecione o motivo..." /></SelectTrigger>
            <SelectContent>
              {motivosDescarte.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Detalhes (opcional)</Label>
          <Textarea
            value={descarteData.detalhe}
            onChange={e => setDescarteData(p => ({ ...p, detalhe: e.target.value }))}
            placeholder="Informações adicionais..."
            rows={3}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button className="bg-red-600 hover:bg-red-700" onClick={onConfirm}>
          Confirmar Descarte
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ConcluidoDialog = ({ open, onOpenChange, concluidoData, setConcluidoData, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
          Concluir Oportunidade
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">Confirme o valor final da venda:</p>
        <div>
          <Label className="text-xs">Valor Final (R$)</Label>
          <CurrencyInput
            value={concluidoData.valor_final}
            onChange={v => setConcluidoData(p => ({ ...p, valor_final: v }))}
          />
        </div>
        <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Checkbox
            id="criar_projeto"
            checked={concluidoData.criar_projeto}
            onCheckedChange={checked => setConcluidoData(p => ({ ...p, criar_projeto: checked }))}
          />
          <div>
            <Label htmlFor="criar_projeto" className="font-medium text-sm cursor-pointer text-blue-700">
              Criar Projeto automaticamente
            </Label>
            <p className="text-xs text-blue-600 mt-0.5">
              Ao marcar, você será direcionado para completar os dados do projeto
            </p>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button className="bg-green-600 hover:bg-green-700" onClick={onConfirm}>
          Confirmar Conclusão
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const CriarProjetoDialog = ({ open, onOpenChange, oportunidade, projetoForm, setProjetoForm, fabricas, loading, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FolderPlus className="h-5 w-5 text-blue-600" />
          Criar Projeto a partir da Venda
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {oportunidade && (
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="font-medium">Oportunidade: {oportunidade.titulo}</div>
            <div className="text-gray-500">
              Cliente: {oportunidade.cliente_nome || oportunidade.contato_nome}
              {oportunidade.embarcacoes?.length > 0 && (
                <span className="ml-2">• {oportunidade.embarcacoes.reduce((a, e) => a + (e.quantidade || 1), 0)} embarcações</span>
              )}
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs">Nome do Projeto *</Label>
          <Input value={projetoForm.name} onChange={e => setProjetoForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Projeto T800 - Marinha" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Fábrica *</Label>
            <Select value={projetoForm.factory_id || 'none'} onValueChange={v => {
              const fab = fabricas.find(f => f.id === v);
              setProjetoForm(p => ({ ...p, factory_id: v === 'none' ? '' : v, factory_name: fab?.name || '' }));
            }}>
              <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Selecionar fábrica</SelectItem>
                {fabricas.map(f => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Responsável Técnico</Label>
            <Input value={projetoForm.responsavel_tecnico} onChange={e => setProjetoForm(p => ({ ...p, responsavel_tecnico: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Data Início</Label>
            <Input type="date" value={projetoForm.start_date} onChange={e => setProjetoForm(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Data Entrega *</Label>
            <Input type="date" value={projetoForm.delivery_date} onChange={e => setProjetoForm(p => ({ ...p, delivery_date: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Valor do Contrato</Label>
            <CurrencyInput value={projetoForm.budget} onChange={v => setProjetoForm(p => ({ ...p, budget: v }))} />
          </div>
          <div>
            <Label className="text-xs">Nº Empenho/Processo</Label>
            <Input value={projetoForm.numero_empenho} onChange={e => setProjetoForm(p => ({ ...p, numero_empenho: e.target.value }))} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Link Google Drive</Label>
          <Input value={projetoForm.google_drive_url} onChange={e => setProjetoForm(p => ({ ...p, google_drive_url: e.target.value }))} placeholder="https://drive.google.com/..." />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={onConfirm} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderPlus className="h-4 w-4 mr-2" />}
          Criar Projeto
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeleteDialog = ({ deleteId, onOpenChange, onConfirm }) => (
  <AlertDialog open={!!deleteId} onOpenChange={() => onOpenChange(null)}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Excluir oportunidade?</AlertDialogTitle>
        <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm}>Excluir</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const NovaTarefaDialog = ({ open, onOpenChange, tarefaForm, setTarefaForm, usuarios, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="dialog-nova-tarefa-inline">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-amber-600" />
          Nova Tarefa
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        {tarefaForm.oportunidade_titulo && (
          <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-600">
            <span className="font-medium">Oportunidade:</span> {tarefaForm.oportunidade_titulo}
          </div>
        )}
        <div>
          <Label className="text-xs">Título *</Label>
          <Input value={tarefaForm.titulo} onChange={e => setTarefaForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex: Ligar para cliente" data-testid="input-tarefa-titulo" />
        </div>
        <div>
          <Label className="text-xs">Descrição</Label>
          <Textarea value={tarefaForm.descricao} onChange={e => setTarefaForm(f => ({ ...f, descricao: e.target.value }))} rows={2} className="text-xs" placeholder="Detalhes da tarefa..." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={tarefaForm.tipo} onValueChange={v => setTarefaForm(f => ({ ...f, tipo: v }))}>
              <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="ligacao">Ligação</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="reuniao">Reunião</SelectItem>
                <SelectItem value="visita">Visita</SelectItem>
                <SelectItem value="enviar_proposta">Enviar Proposta</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Prioridade</Label>
            <Select value={tarefaForm.prioridade} onValueChange={v => setTarefaForm(f => ({ ...f, prioridade: v }))}>
              <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="baixa">Baixa</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Data Vencimento</Label>
            <Input type="date" value={tarefaForm.data_vencimento} onChange={e => setTarefaForm(f => ({ ...f, data_vencimento: e.target.value }))} className="text-xs" />
          </div>
          <div>
            <Label className="text-xs">Responsável</Label>
            <Select value={tarefaForm.responsavel_id || 'nenhum'} onValueChange={v => {
              const user = usuarios.find(u => u.id === v);
              setTarefaForm(f => ({ ...f, responsavel_id: v === 'nenhum' ? '' : v, responsavel_nome: user?.full_name || '' }));
            }}>
              <SelectTrigger className="text-xs"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nenhum">Nenhum</SelectItem>
                {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button className="bg-amber-600 hover:bg-amber-700" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Criar Tarefa
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
